<template>
  <div class="container general-info-container mb-3">
    <div>
      <details class="align-self-start w-100 text-start" :open="!sessionId">
        <summary>
          <h3 class="text-start fs-4 d-inline">General Case Info</h3>
        </summary>

        <div v-if="templates.length !== 0">
          <div class="text-start fs-4 fw-medium mt-2">Choose template</div>
          <div v-for="template in templates" :key="template">
            <label>
              <input v-model="templateName" type="radio" :value="template" />
              {{ template }}
            </label>
          </div>
        </div>

        <div
          v-else-if="selectedTemplateName !== ''"
          class="text-start fs-4 fw-medium mt-2"
        >
          Template: {{ selectedTemplateName }}
        </div>

        <p v-if="!sessionId" class="text-center fw-bold fs-5 ps-0 mb-0 mt-3">
          If necessary, fill in the blanks and change the incorrect data, then
          click "Continue"
        </p>

        <form id="generalInfoForm" class="inputs-grid">
          <div
            v-for="(_, key) in localInputsData"
            :key="key"
            class="d-flex flex-column"
          >
            <label class="pb-2 ps-0 fs-5 text-start fw-medium" :for="key">
              {{ key }}
            </label>
            <input
              :id="key"
              v-model="localInputsData[key]"
              class="p-2 fw-medium form-control"
              required
              pattern="\S+.*"
              type="text"
              :disabled="Boolean(sessionId)"
            />
          </div>
        </form>

        <div class="pb-2 ps-0 fs-5 text-start fw-medium mt-3">
          Additional Info
        </div>
        <div>
          <textarea
            v-model="localAdditionalInfo"
            class="w-100 text-area form-control"
            placeholder="Provide additional information about the case"
            :disabled="Boolean(sessionId)"
          />
        </div>

        <div v-if="!sessionId" class="d-flex flex-column mt-3">
          <button
            v-if="!loading"
            form="generalInfoForm"
            class="btn btn-primary fw-bold fs-6 col-2 mt-3 align-self-center"
            @click="submitGeneralInfo"
          >
            Continue
          </button>
          <button
            v-else
            class="btn btn-primary fw-bold fs-6 col-2 mt-3 align-self-center"
            type="button"
            disabled
          >
            <span
              class="spinner-border spinner-border-sm me-3"
              aria-hidden="true"
            />
            <span role="status">Loading...</span>
          </button>
        </div>
      </details>
    </div>
  </div>
</template>

<script lang="ts">
import { createSession } from '@/common/api';
import { defineComponent } from 'vue';
import { handleError } from '@/common/errors';

export default defineComponent({
  name: 'GeneralInfo',

  props: {
    matterName: {
      type: String,
      required: true,
    },
    inputsData: {
      type: Object,
      required: true,
    },
    sessionId: {
      type: Number,
      required: false,
      default: 0,
    },
    templates: {
      type: Array,
      required: false,
      default: () => [],
    },
    recommendedTemplateName: {
      type: String,
      required: false,
      default: null,
    },
    selectedTemplateName: {
      type: String,
      required: false,
      default: null,
    },
    additionalInfo: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      loading: false,
      error: {} as Error | null,
      localInputsData: this.inputsData,
      templateName: this.recommendedTemplateName,
      localAdditionalInfo: this.additionalInfo,
    };
  },

  methods: {
    async submitGeneralInfo() {
      const generalInfo = {
        matterInfo: {
          matterName: this.matterName,
          email: this.inputsData['Email'],
          insuranceCompanyName: this.inputsData['Insurance Company Name'],
          address: this.inputsData['Address'],
          adjusterName: this.inputsData['Adjuster Name'],
          adjusterSalutation: this.inputsData['Adjuster Salutation'],
          phone: this.inputsData['Phone'],
          clientName: this.inputsData['Client Name'],
          defendantName: this.inputsData['Defendant Name'],
          claimNumber: this.inputsData['Claim Number'],
          incidentDate: this.inputsData['Incident Date'],
          affordedInPip: Number(this.inputsData['Afforded in PIP']),
          additionalInfo: this.localAdditionalInfo,
        },
        damage: Number(this.inputsData['Damage']),
        templateName: this.templateName,
      };

      this.loading = true;
      this.error = null;

      try {
        const response = await createSession(generalInfo);
        this.$router.push(`/demand-letter?session-id=${response.data.id}`);
      } catch (error: unknown) {
        handleError(error, 'Failed to create session');
        this.loading = false;
      }
    },
  },
});
</script>

<style scoped>
.inputs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
  margin-top: 1rem;
}

input:invalid {
  border: 1px #dc3545 solid;
}

input:invalid:focus {
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.5);
}

.text-area {
  height: 600px;
}

.text-area:disabled::placeholder {
  color: transparent;
}
</style>
